import { Fragment, useEffect, useState } from 'react'
import type { AgentPreferences, RunnerAgentSettings, RunnerOwnSettings } from '../api/types'
import { errorMessage } from '../api/client'
import { canKeepClaudeApiKey, keepClaudeApiKey } from '../api/shellClaudeApiKey'
import { agentName } from '../code/format'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'
import { useRunners, useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { CommandRules } from './CommandRules'
import { LocalModels } from './LocalModels'
import { QueueCaches } from './QueueCaches'

export function AgentSettings() {
  const store = useStore()
  const { me, offline } = useBoardState()
  const { runners } = useRunners()
  const [selected, select] = useState('')
  if (store.byToken || !me?.member) return null
  const listed = (runners ?? []).filter(r => !r.revoked)
  const runner = listed.some(r => r.id === selected) ? selected : listed[0]?.id || ''
  return <section className="settings-section">
    <label className="field"><span>Runner</span><select value={runner} onChange={e => select(e.target.value)}>
      {!listed.length && <option value="">No runners</option>}
      {listed.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
    </select></label>
    <p className="muted">These settings apply to new sessions on this machine.</p>
    {runner && <RunnerSettings key={runner} runner={runner} unavailable={offline || !listed.find(r => r.id === runner)?.connected} />}
  </section>
}
function RunnerSettings({ runner, unavailable }: { runner: string; unavailable: boolean }) {
  const { api } = useSessionsEnv()
  const [rows, setRows] = useState<RunnerAgentSettings[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [attempt, retry] = useState(0)
  const [reading, setReading] = useState(false)
  useEffect(() => {
    let active = true
    setReading(true)
    setProblem(null)
    if (unavailable || !api.agentSettings) { setReading(false); setProblem('The runner is unavailable.'); return }
    api.agentSettings(runner).then(rows => { if (active) setRows(rows) }, error => { if (active) setProblem(errorMessage(error)) }).finally(() => { if (active) setReading(false) })
    return () => { active = false }
  }, [runner, unavailable, attempt, api])
  /** The runner's rows after a save, each unchanged row keeping its object, so an open form is not reset. */
  const keep = (saved: RunnerAgentSettings[]) => setRows(current => saved.map(next => {
    const previous = current?.find(held => held.agent === next.agent)
    return previous && JSON.stringify(previous.preferences) === JSON.stringify(next.preferences) ? { ...next, preferences: previous.preferences } : next
  }))
  return <>
    <button type="button" className="button button-small" disabled={reading || unavailable} onClick={() => retry(n => n + 1)}>Refresh</button>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
    <SessionLimit key={attempt} runner={runner} unavailable={unavailable} />
    <QueueCaches key={`cache/${attempt}`} runner={runner} unavailable={unavailable} />
    <LocalModels key={`models/${attempt}`} runner={runner} unavailable={unavailable} />
    {reading && <p role="status" className="muted">Reading agents…</p>}
    {rows?.length === 0 && <p className="muted">No agent programs found.</p>}
    {rows?.map(row => <Fragment key={`${row.agent}/${attempt}`}><AgentForm row={row} unavailable={unavailable || reading} save={async preferences => {
      if (!api.setAgentSettings) throw new Error('The runner is unavailable.')
      const saved = await api.setAgentSettings(runner, { agent: row.agent, expected: row.preferences, preferences })
      keep(saved)
    }} />
    {row.command_rules && <CommandRules rules={row.command_rules} unavailable={unavailable || reading} change={async change => {
      if (!api.changeCommandRule) throw new Error('The runner is unavailable.')
      const saved = await api.changeCommandRule(runner, { agent: row.agent, ...change })
      keep(saved)
    }} />}
    {row.api_key_set !== undefined && <ClaudeApiKey runner={runner} set={row.api_key_set} unavailable={unavailable || reading} changed={() => retry(n => n + 1)} />}
    </Fragment>)}
  </>
}
/**
 * Claude API (Poulpe-585): Claude Code billed to an API key rather than the
 * plan. The key is write-only. The Mac app keeps it in this Mac's Keychain,
 * for the runner on this Mac, and the runner says only whether one is set.
 * The editor shows for that runner alone (Poulpe-718): another runner's key
 * is set on its own machine, and a browser can only say whether one is.
 */
function ClaudeApiKey({ runner, set, unavailable, changed }: { runner: string; set: boolean; unavailable: boolean; changed: () => void }) {
  const [field, setField] = useState('')
  const [replacing, setReplacing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  useUnsavedChanges(field !== '')
  const local = canKeepClaudeApiKey(runner)
  const entering = !set || replacing
  async function keep(key: string | null) {
    if (busy || unavailable) return
    setBusy(true); setProblem(null)
    try {
      await keepClaudeApiKey(runner, key)
      setField(''); setReplacing(false)
      toast(key === null ? 'Removed from this Mac.' : "Saved in this Mac's Keychain.")
      changed()
    } catch (error) { setProblem(errorMessage(error)) }
    finally { setBusy(false) }
  }
  return <form className="setup-form" aria-label="Claude API key" onSubmit={e => { e.preventDefault(); if (field.trim()) void keep(field.trim()) }}>
    <fieldset className="settings-group" disabled={busy || unavailable}>
      <legend>Claude API</legend>
      <p className="muted">{set ? 'An API key is set on this machine: sessions can run as Claude API.' : 'No API key is set on this machine.'}</p>
      {!local && <p className="muted">The key is set in the Poulpe app on that machine.</p>}
      {local && entering && <label className="field"><span>API key</span><input type="password" autoComplete="off" spellCheck={false} value={field} onChange={e => { setField(e.target.value); setProblem(null) }} /></label>}
      {local && entering && <button className="button button-primary" disabled={!field.trim()}>{busy ? 'Saving…' : 'Save'}</button>}
      {local && set && !replacing && <button type="button" className="button" onClick={() => setReplacing(true)}>Replace</button>}
      {local && set && <button type="button" className="button" onClick={() => void keep(null)}>Remove</button>}
    </fieldset>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </form>
}
/** The runner's range (`poulpe_protocol::runner_settings`), as today's app's: the runner is the judge. */
const LEAST = 1
const MOST = 64
const WRONG = `Choose a whole number from ${LEAST} to ${MOST}.`
const whole = (text: string) => /^\d+$/.test(text.trim()) && Number(text) >= LEAST && Number(text) <= MOST
/**
 * How many of the sessions a person starts run at once on this runner
 * (Poulpe-498), as today's Session limit: workers, reviews and PM agents take
 * no place. It is read again as each one starts, so a lower number stops
 * nothing that runs. A save names the value it replaces, as agents' do.
 */
function SessionLimit({ runner, unavailable }: { runner: string; unavailable: boolean }) {
  const { api } = useSessionsEnv()
  const [saved, setSaved] = useState<RunnerOwnSettings | null>(null)
  const [field, setField] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const dirty = saved !== null && field.trim() !== String(saved.capacity)
  useUnsavedChanges(dirty)
  useEffect(() => {
    let active = true
    if (unavailable || !api.runnerSettings) return
    api.runnerSettings(runner).then(read => { if (active) { setSaved(read); setField(String(read.capacity)) } }, error => { if (active) setProblem(errorMessage(error)) })
    return () => { active = false }
  }, [runner, unavailable, api])
  if (!saved) return problem ? <p role="alert" className="text-danger">{problem}</p> : null
  async function submit(expected: RunnerOwnSettings) {
    if (busy || unavailable || uncertain || !dirty) return
    if (!whole(field)) { setProblem(WRONG); return }
    setBusy(true); setProblem(null)
    try {
      if (!api.setRunnerSettings) throw new Error('The runner is unavailable.')
      const next = await api.setRunnerSettings(runner, { expected, settings: { capacity: Number(field) } })
      setSaved(next); setField(String(next.capacity)); toast('Saved.')
    } catch (error) { setProblem(`${errorMessage(error)} Refresh before trying again.`); setUncertain(true) }
    finally { setBusy(false) }
  }
  return <form className="setup-form" aria-label="Session limit" noValidate onSubmit={e => { e.preventDefault(); void submit(saved) }}>
    <fieldset className="settings-group" disabled={busy || unavailable || uncertain}>
      <label className="field"><span>Session limit</span><input type="number" inputMode="numeric" min={LEAST} max={MOST} step="1" value={field}
        onChange={e => { setField(e.target.value); setProblem(null) }} /></label>
      <button className="button button-primary" disabled={!dirty}>{busy ? 'Saving…' : 'Save'}</button>
    </fieldset>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </form>
}
function AgentForm({ row, unavailable, save }: { row: RunnerAgentSettings; unavailable: boolean; save: (value: AgentPreferences) => Promise<void> }) {
  const [draft, setDraft] = useState(row.preferences)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const dirty = JSON.stringify(draft) !== JSON.stringify(row.preferences)
  useUnsavedChanges(dirty)
  useEffect(() => { setDraft(row.preferences) }, [row.preferences])
  const defaults = draft.defaults
  const catalogue = row.catalogue
  const modelOption = catalogue?.options?.find(o => o.category === 'model')
  const models = modelOption?.choices ?? Object.entries(catalogue?.model_details ?? {}).map(([value, detail]) => ({ value, name: detail.name ?? value }))
  const effort = defaults.model ? catalogue?.model_details?.[defaults.model]?.effort : catalogue?.options?.find(o => o.category === 'thought_level' || o.category === 'thinking')
  const change = (key: 'model' | 'mode', value: string) => setDraft({ ...draft, defaults: { ...defaults, [key]: value || undefined, ...(key === 'model' ? { effort: undefined } : {}) } })
  async function submit() {
    if (busy || unavailable || uncertain || !dirty) return
    setBusy(true); setProblem(null)
    try { await save(draft); toast('Saved.') }
    catch (error) { setProblem(errorMessage(error)); setUncertain(true) }
    finally { setBusy(false) }
  }
  return <form className="setup-form" onSubmit={e => { e.preventDefault(); void submit() }}>
    <fieldset className="settings-group" disabled={busy || unavailable || uncertain}>
      <legend>{row.agent === 'claude' ? 'Claude Code' : agentName(row.agent)}</legend>
      <label><input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} /> Enable</label>
      <label className="field"><span>Program</span><select value={draft.program} onChange={e => setDraft({ ...draft, program: e.target.value })}>
        {row.programs.map(program => <option key={program} value={program}>{program}</option>)}
      </select></label>
      <label className="field"><span>Default model</span><select value={defaults.model ?? ''} onChange={e => change('model', e.target.value)}>
        <option value="">Default</option>
        {defaults.model && !models.some(m => m.value === defaults.model) && <option value={defaults.model}>{defaults.model}</option>}
        {models.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
      </select></label>
      <label className="field"><span>Default effort</span><select value={defaults.effort?.value ?? ''} onChange={e => setDraft({ ...draft, defaults: { ...defaults, effort: e.target.value && effort ? { id: effort.id, value: e.target.value } : undefined } })}>
        <option value="">Default</option>
        {defaults.effort && !effort?.choices.some(c => c.value === defaults.effort?.value) && <option value={defaults.effort.value}>{defaults.effort.value}</option>}
        {effort?.choices.map(c => <option key={c.value} value={c.value}>{c.name}</option>)}
      </select></label>
      <label className="field"><span>Default permissions</span><select value={defaults.mode ?? ''} onChange={e => change('mode', e.target.value)}>
        <option value="">Default</option>
        {defaults.mode && !catalogue?.modes?.some(m => m.value === defaults.mode) && <option value={defaults.mode}>{defaults.mode}</option>}
        {catalogue?.modes?.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
      </select></label>
      {row.metadata_error === 'too_large' && <p className="muted">This agent’s models and modes are too many to list here. Saved defaults are kept.</p>}
      <p className="muted">{row.signed_in === true ? 'Signed in on this machine' : row.signed_in === false ? 'Not signed in on this machine' : 'Sign-in status unknown'}</p>
      {row.sign_in && <p className="muted">Sign in on this machine: <code>{row.sign_in}</code></p>}
      {row.update && <p className="muted">Update on this machine: <code>{row.update}</code></p>}
      <button className="button button-primary" disabled={!dirty}>{busy ? 'Saving…' : 'Save'}</button>
    </fieldset>
    {problem && <p role="alert" className="text-danger">{problem} Refresh before trying again.</p>}
  </form>
}
